var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { Alert, Button, Chip, Dialog, DialogActions, DialogContent, Grid, Link } from '@mui/material';
import { styled } from '@mui/material/styles';
import { previewReminderEmails, sendDashboardEmails } from '../rest/BackendApi';
import Loading from './Loading';
import { Link as RouterLink } from 'react-router-dom';
import 'react-quill/dist/quill.snow.css';
import 'react-quill/dist/quill.bubble.css';
import Editor from './Editor/Editor';
import { EMAIL_TAGS } from '../model/EmailTags';
import Checkbox from '@mui/material/Checkbox';
const BlurryDialog = styled(Dialog)(({ theme }) => ({
    backdropFilter: 'blur(2px)',
}));
const EmailSendingDialog = (props) => {
    const { dashboardIds, open, onClose } = props;
    const [emailsPreview, setEmailsPreview] = useState(null);
    const [bodyTemplate, setBodyTemplate] = useState(null);
    const [attachTransactions, setAttachTransactions] = useState(false);
    const [subjectTemplate, setSubjectTemplate] = useState(null);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        if (dashboardIds) {
            setLoading(true);
            previewReminderEmails(dashboardIds)
                .then(response => {
                setEmailsPreview(response);
                setBodyTemplate(response.body);
                setSubjectTemplate(response.subject);
                setLoading(false);
            })
                .catch(reason => {
                onClose({ error: "Couldn't generate email preview" });
                setLoading(false);
            });
        }
    }, [dashboardIds]);
    function sendEmails(dashboardIds) {
        return __awaiter(this, void 0, void 0, function* () {
            if (bodyTemplate == null || bodyTemplate.length == 0) {
                onClose({ error: `Email content was empty. Skipped` });
                return;
            }
            if (subjectTemplate == null || subjectTemplate.length == 0) {
                onClose({ error: `Email subject was empty. Skipped` });
                return;
            }
            setLoading(true);
            try {
                const results = yield sendDashboardEmails(dashboardIds, subjectTemplate, bodyTemplate, attachTransactions);
                const success = [...results.entries()].filter(([key, value]) => value === 'SUCCESS');
                const idsInError = [...results.entries()].filter(([key, value]) => value !== 'SUCCESS').map(([key]) => key);
                if (success.length > 0 && idsInError.length === 0) {
                    onClose({ success: `Successfully sent ${success.length} reminder email(s)` });
                }
                else if (success.length > 0) {
                    onClose({
                        error: `Successfully sent ${success.length} reminder email(s) but failed for the other organisation(s). Make sure they have a contact set.`,
                    });
                }
                else {
                    onClose({ error: 'Failed to send reminder emails, please check the contacts are set on those organisations' });
                }
            }
            catch (error) {
                onClose({ error: 'Failed to send reminder emails, ' + error });
            }
            finally {
                setLoading(false);
            }
        });
    }
    if (!open) {
        return;
    }
    if (!emailsPreview || loading) {
        return _jsx(Loading, {});
    }
    function getRecipientWithMissingConfig(recipients) {
        return recipients.filter(recipient => recipient.email == undefined || recipient.managerEmail == undefined);
    }
    function getValidRecipients(recipients) {
        return recipients.filter(recipient => recipient.email != undefined && recipient.managerEmail != undefined);
    }
    const hasValidRecipients = emailsPreview && getValidRecipients(emailsPreview.to).length > 0;
    const nbMissingConfig = getRecipientWithMissingConfig(emailsPreview.to).length;
    const maxMissingToShow = 5;
    const ContactNotSetBanner = () => {
        if (!hasValidRecipients) {
            return (_jsxs(Alert, { severity: "error", style: { marginBottom: '20px' }, children: ["None of the organisation(s) selected have a contact and/or client manager set. ", _jsx("br", {}), "Go to the", ' ', _jsx(Link, { component: RouterLink, to: "/organisations", children: "Organisations screen" }), ' ', "to set them first."] }));
        }
        else if (nbMissingConfig > 0) {
            return (_jsxs(Alert, { severity: "warning", style: { marginBottom: '20px' }, children: ["Contact and/or client manager are not set for ", nbMissingConfig, " organisation", nbMissingConfig > 1 && 's', ".", _jsx("br", {}), getRecipientWithMissingConfig(emailsPreview.to)
                        .slice(0, maxMissingToShow)
                        .map(org => (_jsx("span", { style: { fontStyle: 'italic', marginRight: 1 }, children: org.organisationName }, org.organisationName)))
                        .reduce((accu, elem) => {
                        return accu === null ? [elem] : [...accu, ', ', elem];
                    }, null), nbMissingConfig > maxMissingToShow && (_jsxs("span", { style: { fontStyle: 'italic', marginRight: 1, marginLeft: 1 }, children: ["... & ", nbMissingConfig - maxMissingToShow, " more"] })), _jsx("br", {}), "Go to the", ' ', _jsx(Link, { component: RouterLink, to: "/organisations", children: "Organisations screen" }), ' ', "to set them first."] }));
        }
    };
    return (_jsxs(BlurryDialog, { transitionDuration: 0, hideBackdrop: true, open: open, onClose: () => onClose(), "aria-labelledby": "confirm-dialog", children: [emailsPreview && (_jsxs(DialogContent, { sx: { padding: '10px' }, children: [_jsx(ContactNotSetBanner, {}), hasValidRecipients && (_jsxs(Grid, { container: true, spacing: 2, children: [_jsx(Grid, { item: true, xs: 2, children: "From:" }), _jsx(Grid, { item: true, xs: 10, children: _jsx("b", { children: emailsPreview.from }) }), _jsx(Grid, { item: true, xs: 2, children: "Reply to:" }), _jsx(Grid, { item: true, xs: 10, children: _jsx("i", { children: emailsPreview.replyTo }) }), _jsx(Grid, { alignContent: "center", item: true, xs: 2, children: "To:" }), _jsx(Grid, { item: true, xs: 10, children: getValidRecipients(emailsPreview.to).map(recipient => {
                                    return (_jsx(Chip, { sx: { height: 24, mb: 1, mr: 1 }, variant: "outlined", color: "primary", label: recipient.organisationName }, recipient.dashboardId));
                                }) }), _jsx(Grid, { alignContent: "center", item: true, xs: 2, children: "Subject:" }), _jsx(Grid, { item: true, xs: 10, children: _jsx(Editor, { onContentUpdate: setSubjectTemplate, initialState: emailsPreview.subject, isHtml: false, tags: EMAIL_TAGS }) }), _jsx(Grid, { item: true, xs: 12, children: _jsx(Editor, { onContentUpdate: setBodyTemplate, initialState: emailsPreview.body, isHtml: true, showToolbar: true, tags: EMAIL_TAGS }) }), _jsxs(Grid, { item: true, xs: 12, children: [_jsx(Checkbox, { onChange: e => setAttachTransactions(e.target.checked), checked: attachTransactions, color: "primary", inputProps: { 'aria-label': 'secondary checkbox' } }), "Attach bank transactions as csv"] })] }))] })), _jsxs(DialogActions, { children: [_jsx(Button, { variant: "outlined", onClick: () => onClose(), color: "inherit", children: "Cancel" }), hasValidRecipients && (_jsx(Button, { disabled: bodyTemplate == null || bodyTemplate.length == 0 || subjectTemplate == null || subjectTemplate.length == 0, variant: "contained", onClick: () => __awaiter(void 0, void 0, void 0, function* () {
                            if (dashboardIds) {
                                setLoading(true);
                                yield sendEmails(getValidRecipients(emailsPreview.to).map(recipient => recipient.dashboardId));
                                setLoading(false);
                            }
                        }), sx: { backgroundColor: '#007ca3' }, children: "Send" }))] })] }));
};
export default EmailSendingDialog;
